<script setup lang="ts">
import { computed } from "vue";
import {
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline";
import type { JobStatus } from "../composables/useJobs";

const props = defineProps<{
  status: JobStatus;
  /** When true, omits the label text and renders only the icon. */
  iconOnly?: boolean;
}>();

const presets: Record<
  JobStatus,
  { icon: typeof CheckCircleIcon; label: string; classes: string; spin: boolean }
> = {
  pending_upload: {
    icon: ClockIcon,
    label: "Pending",
    classes: "bg-info-bg border-info-border text-info",
    spin: false,
  },
  processing: {
    icon: ArrowPathIcon,
    label: "Processing",
    classes: "bg-warning-bg border-warning-border text-warning",
    spin: true,
  },
  completed: {
    icon: CheckCircleIcon,
    label: "Completed",
    classes: "bg-success-bg border-success-border text-success",
    spin: false,
  },
  failed: {
    icon: XCircleIcon,
    label: "Failed",
    classes: "bg-error-bg border-error-border text-error",
    spin: false,
  },
};

const preset = computed(() => presets[props.status]);
</script>

<template>
  <span
    :data-testid="`status-badge-${status}`"
    :class="[
      'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
      preset.classes,
    ]"
  >
    <component
      :is="preset.icon"
      class="h-3.5 w-3.5"
      :class="preset.spin ? 'animate-spin' : ''"
      aria-hidden="true"
    />
    <span v-if="!iconOnly">{{ preset.label }}</span>
  </span>
</template>
